import type { CSSProperties } from "react";
import { type CssTiming, type RevealTiming, reelTiming } from "@/lib/reveal";

/** 한 행 높이. 이동 거리를 아는 유일한 곳이다 — keyframe 은 `--slot-travel` 로 받는다. */
const ROW_H = 40;
/** 당첨자 앞을 지나가는 들러리 행 수. 고정이라 이동 거리도 상수다. */
const LEAD_ROWS = 11;

/** 스트립을 만든다: 들러리 여러 행 → 당첨자 → 들러리 한 행.
 *
 * 들러리는 참여자 이름을 순환시켜 채운다. 실제 후보가 지나가야 뽑는 것처럼 보인다 —
 * 무작위를 쓰지 않는 건 의도다. **난수는 결과에만 쓴다.**
 *
 * 마지막에 한 행을 더 두는 이유: 멈출 때 살짝 지나쳤다 되돌아오는 반동이 있어서,
 * 당첨자가 마지막 행이면 그 순간 빈칸이 보인다.
 */
function strip(pool: readonly string[], winner: string, offset: number): string[] {
  const names = pool.length > 0 ? pool : [winner];
  const filler = (i: number) => names[(i + offset) % names.length];
  return [
    ...Array.from({ length: LEAD_ROWS }, (_, i) => filler(i)),
    winner,
    filler(LEAD_ROWS),
  ];
}

/** 이름이 위로 흘러가다 당첨자에서 멈추는 슬롯 한 칸.
 *
 * 멈추는 시각은 `reelTiming` 이 정하고 그건 `revealAt` 이 값을 확정하는 시각과 같다 —
 * 슬롯이 멈추기 전에 결과가 확정돼 보이면 연출이 거짓말이 된다.
 */
export function SlotReel({
  pool,
  winner,
  index,
  timing,
  spinning,
}: {
  /** 후보 전원. 흘러가는 들러리 이름의 출처다. */
  pool: string[];
  winner: string;
  /** 몇 번째 슬롯인지. 순서대로 멈춘다. */
  index: number;
  timing: RevealTiming;
  /** false 면 연출 없이 당첨자만 보여준다(`prefers-reduced-motion`, `바로 보기`). */
  spinning: boolean;
}) {
  if (!spinning) {
    return (
      <div className="flex items-center" style={{ height: ROW_H }}>
        <span className="truncate text-body-1 font-semibold text-label-normal">{winner}</span>
      </div>
    );
  }

  const css: CssTiming = reelTiming(index, timing);
  const rows = strip(pool, winner, index);

  return (
    <div className="overflow-hidden" style={{ height: ROW_H }}>
      <div
        className="animate-slot-land motion-reduce:animate-none"
        style={
          {
            "--slot-travel": `-${LEAD_ROWS * ROW_H}px`,
            animationDuration: `${css.durationMs}ms`,
            animationDelay: `${css.delayMs}ms`,
          } as CSSProperties
        }
      >
        {rows.map((name, row) => (
          <div
            // 같은 이름이 여러 행에 온다(순환이라 당연하다) — 행 번호를 key 에 넣는다.
            key={`${row}-${name}`}
            className="flex items-center"
            style={{ height: ROW_H }}
          >
            <span
              className={
                // 당첨자 행만 강조한다. 지나가는 이름과 확정된 이름이 같아 보이면
                // 멈추기 전에 이미 정해진 것처럼 읽힌다.
                row === LEAD_ROWS
                  ? "truncate text-body-1 font-semibold text-label-normal"
                  : "truncate text-body-1 text-label-assistive"
              }
            >
              {name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
